import get from "lodash.get";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { pickCommaSeparate } from "../helpers";
import { useScript } from "../hooks";
import Input from "./Input";
import InputField from "./InputField";

/*
  If this component is written in functional style, there's a problem with `onPlaceChange` giving
  stale results, because it's inside event handler. Normally you'd use useEffect to refresh values
  when they change, but Google Autocomplete won't cleanup extra dropown nodes which leads to some
  odd behaviour, multiple dropdowns being renderd etc. Beware.
*/

class InputAddress extends Component {
  constructor(props) {
    super(props);

    this.ref = React.createRef();
    this.handlePlaceChange = this.handlePlaceChange.bind(this);
  }

  componentDidMount() {
    const node = this.ref.current;

    this.autocomplete = new window.google.maps.places.Autocomplete(node, {
      types: ["geocode"],
      componentRestrictions: { country: "uk" },
    });

    this.listener = this.autocomplete.addListener("place_changed", this.handlePlaceChange);
  }

  componentWillUnmount() {
    window.google.maps.event.removeListener(this.listener);
  }

  handlePlaceChange() {
    const { onPlaceChange } = this.props;
    const place = this.autocomplete.getPlace();
    const address = get(place, "address_components", []).reduce(
      (prev, next) => ({ ...prev, [next.types[0]]: next.long_name }),
      {}
    );

    const line1elements = ["premise", "street_number", "route"];
    const line2elements = ["street_address", "locality", "sublocality", "neighborhood"];

    const newPlace = {
      line1: pickCommaSeparate(address, line1elements),
      line2: pickCommaSeparate(address, line2elements) || undefined,
      postCode: address.postal_code,
      city: address.postal_town,
      county: address.administrative_area_level_2,
      country: address.country,
    };

    onPlaceChange(newPlace);
  }

  render() {
    const { className, descriptionText, labelText, onPlaceChange, ...rest } = this.props;

    return (
      <InputField
        className={className}
        descriptionText={descriptionText}
        labelText={labelText}
        {...rest}
      >
        <Input ref={this.ref} {...rest} />
      </InputField>
    );
  }
}

const key = process.env.GOOGLE_PLACES_API_KEY;
const url = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;

const InputAddressWrapper = (props) => {
  const [loaded] = useScript(url);

  if (!loaded || !window.google) {
    return false;
  }

  return <InputAddress {...props} />;
};

InputAddress.propTypes = {
  className: PropTypes.string,
  descriptionText: PropTypes.string,
  labelText: PropTypes.string,
  onPlaceChange: PropTypes.func.isRequired,
};

InputAddress.defaultProps = {
  className: undefined,
  descriptionText: undefined,
  labelText: undefined,
};

export default InputAddressWrapper;
